import type { UniverseConfig, UniverseProfile } from "@/lib/universe";

export type ArchetypeId = "divergent" | "ascendant" | "strange" | "unexpected";
export const ARCHETYPE_ORDER: ArchetypeId[] = ["divergent", "ascendant", "strange", "unexpected"];

export type InterestId = "science" | "art" | "music" | "nature" | "technology" | "people" | "stories" | "adventure";
export const INTEREST_ORDER: InterestId[] = [
  "science",
  "art",
  "music",
  "nature",
  "technology",
  "people",
  "stories",
  "adventure",
];

export type StrengthId = "curiosity" | "empathy" | "discipline" | "creativity" | "courage" | "logic" | "humor" | "patience";
export const STRENGTH_ORDER: StrengthId[] = [
  "curiosity",
  "empathy",
  "discipline",
  "creativity",
  "courage",
  "logic",
  "humor",
  "patience",
];

export type AmbitionId = "understand" | "build" | "help" | "lead" | "create" | "wander";
export const AMBITION_ORDER: AmbitionId[] = ["understand", "build", "help", "lead", "create", "wander"];

export type LifePathId = "rooted" | "scholar" | "maker" | "voyager" | "guide" | "founder";
export const LIFE_PATH_ORDER: LifePathId[] = ["rooted", "scholar", "maker", "voyager", "guide", "founder"];

export type AxisId = "curiosity" | "connection" | "courage" | "stability" | "influence";
export const AXIS_ORDER: AxisId[] = ["curiosity", "connection", "courage", "stability", "influence"];

export interface SelvesInput {
  nickname: string;            // sanitized, "" = none given (display name falls back to "Traveler")
  interests: InterestId[];     // 0..3, unique, in canonical option order
  strengths: StrengthId[];     // 0..3, unique, canonical order
  ambition: AmbitionId | null;
  lifePath: LifePathId | null;
}

export interface SelvesState {
  input: SelvesInput;
  variants: [number, number, number, number]; // integer 0..999999 per archetype, index = ARCHETYPE_ORDER
  universe: UniverseConfig | null;            // null = each timeline has its own universe; set = all four live in this one
}

export type UniverseCondition = "hospitable" | "marginal" | "hostile"; // from profile.habitability: >=55 / 20..54 / <20

export interface Trait { name: string; description: string; level: number } // level 0..100
export interface LifeChoice { age: string; choice: string; roadNotTaken: string; consequence: string }
export interface WorldDetail { label: string; value: string }

export interface Relationship {
  who: string;  // a name
  role: string; // e.g. "Mentor", "Rival", "Sibling"
  bond: string; // 1-2 sentences
}

export interface Dossier {
  background: string;                 // 2-4 sentence background paragraph (family, place/era, education/formation)
  personality: string;                // 2-3 sentences, specific
  skills: string[];                   // 4-6 short skill phrases
  motivations: string[];              // 2-3 sentences/phrases
  relationships: Relationship[];      // exactly 3
  achievements: string[];             // 2-3 sentences
  failures: string[];                 // exactly 2 sentences
  dilemma: string;                    // the defining dilemma, 2-3 sentences, an open tension
  trajectory: { nearFuture: string; longArc: string }; // each 1-3 sentences
  influence: string[];                // 4-6 sentences explaining how each of the visitor's answers (and each skipped answer the generator filled in) shaped THIS self
}

export interface ResolvedInput {      // the visitor's answers with skipped fields filled by seeded picks
  interests: InterestId[];            // 1..3, canonical order
  strengths: StrengthId[];            // 1..3, canonical order
  ambition: AmbitionId;
  lifePath: LifePathId;
  filled: { interests: boolean; strengths: boolean; ambition: boolean; lifePath: boolean }; // true = generator chose it because the visitor skipped it
}

export interface SelfVisual {
  seed: number;
  hue: number;      // 0..359
  hue2: number;     // 0..359
  motif: "spiral" | "lattice" | "orbits" | "rift" | "bloom" | "tide"; // archetype-biased, seeded
}

export interface LifeGlance { age: number; home: string; society: string; standing: string } // FREE preview; each a short phrase (<= 90 chars)
export interface LifeBeat { time: string; heading: string; text: string }
export interface LifeCareerStage { stage: string; years: string; text: string }
export interface LifeFuturePath { id: "a" | "b" | "c"; name: string; flavor: "Most natural" | "Ambitious" | "Wildcard"; summary: string; opportunities: string[]; challenges: string[]; consequences: string }
export type LifeTimelineKind = "origin" | "education" | "choice" | "achievement" | "setback" | "turning" | "present" | "future";
export interface LifeTimelineEntry { age: number; label: string; title: string; text: string; kind: LifeTimelineKind }
export type EarthDimension = "Occupation" | "Social status" | "Financial circumstances" | "Relationships" | "Freedoms" | "Daily life" | "Available technology" | "Future opportunities";
export interface EarthRow { dimension: EarthDimension; here: string; earth: string; because: string; divergenceId: string }
export interface EarthDivergence { id: string; title: string; text: string; effects: string }
export interface LifeRecord {
  engineVersion: string;
  identity: { fullName: string; age: number; ageNote: string; personalitySummary: string; biography: string[] }; // biography 3-5 paragraphs
  work: { occupation: string; expertise: string[]; careerHistory: LifeCareerStage[]; responsibilities: string[]; workplace: string };
  standing: { wealthBracket: string; wealthText: string; socialClass: string; reputation: string; prestige: string; influence: string; influenceLevel: number; economicCircumstances: string };
  home: { dwelling: string; environment: string; lifestyle: string; possessions: string[]; transport: string; routine: string[] };
  people: { family: string; relationships: string; friendships: string; socialCircle: string };
  formative: { childhood: string; education: string; decisions: string[]; achievements: string[]; setbacks: string[]; turningPoints: string[] };
  society: { summary: string; laws: string; culture: string; institutions: string; expectations: string; opportunity: string };
  world: { technology: string; science: string; geography: string; history: string; environment: string };
  howDiffers: { headline: string; paragraphs: string[]; keyDifferences: { title: string; text: string }[] };
  dayInLife: { title: string; beats: LifeBeat[]; closing: string };
  futures: { title: string; paths: LifeFuturePath[]; disclaimer: string };
  timeline: LifeTimelineEntry[];
  earthComparison: { basis: "general-reference"; basisNote: string; rows: EarthRow[]; divergences: EarthDivergence[]; visitorSignals: string[] };
}

export interface AlternateSelf {
  archetype: ArchetypeId;
  scenarioLabel: string;       // "A Different Life Path" | "An Advanced Future Civilization" | "A Radically Different World" | "An Unexpected Identity"
  name: string;                // unique personal name or title, e.g. "Ilvane Roth"; weaves in nickname where natural
  title: string;               // epithet, e.g. "Cartographer of Quiet Harbours"
  timelineId: string;          // e.g. "TL-4F9A21-Δ2" unique per self and per variant
  tagline: string;
  occupation: string;
  traits: Trait[];             // exactly 4
  axes: Record<AxisId, number>;// 0..100
  story: { origin: string; turningPoint: string; today: string };
  choices: LifeChoice[];       // exactly 3
  worldDetails: WorldDetail[]; // 4..6
  dossier: Dossier;
  resolved: ResolvedInput;
  universe: {
    name: string; code: string; description: string;
    condition: UniverseCondition;
    config: UniverseConfig;
    profile: UniverseProfile;  // from generateUniverse(config)
  };
  differences: {
    summary: string;           // what makes this self unlike the other versions (2-3 sentences)
    versus: { other: ArchetypeId; text: string }[]; // exactly 3, one per other self
  };
  visual: SelfVisual;
  lifeGlance: LifeGlance;
  life: LifeRecord | null;
}

export interface AxisComparison { axis: AxisId; label: string; a: number; b: number; delta: number; text: string }
export interface SelfComparison {
  summary: string;
  axes: AxisComparison[];            // 5, AXIS_ORDER
  sharedGround: string;
  biggestDivergence: string;
  universeContrast: string;
  choiceContrast: string;
}
